/**
 * A drafting or research run that failed, as the attorney should read it: what
 * went wrong in plain words, whether the draft kept its document, and Retry.
 * The runtime's own text stays in the row's fold and this card's tooltip.
 */
import { AlertTriangle, ExternalLink, RotateCcw } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";

import { friendlyRunFailure } from "../../workspace/workspaceModel";
import { useOfferActionsContext } from "../offers/OfferActionsContext";

export type RunFailure = {
  reason: string;
  draftId: string | null;
  title: string | null;
  keptDocument: boolean;
  research: boolean;
  /** The typed failure code, when the backend recorded one. */
  code: string | null;
  /** False when starting the same run again cannot help. */
  retryable: boolean;
  /** The instruction the failed run was given, which Retry replays. */
  instructions: string | null;
};

const RUN_TOOLS = new Set(["generate_draft", "start_research"]);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

const text = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v.trim() : null;

/**
 * The failure a `generate_draft` / `start_research` call reported, or null.
 * Only a run that started and failed carries `run`; a refusal before any run
 * (a stale Notice Brief, an unconfirmed notice) is for the agent to fix, so it
 * stays an ordinary error row.
 */
export const runFailureOf = (
  name: string,
  error: string | undefined,
  output: unknown
): RunFailure | null => {
  if (!error || !RUN_TOOLS.has(name)) return null;
  const started =
    (isRecord(output) && isRecord(output.run)) ||
    /could not be started/i.test(error);
  if (!started) return null;
  const draft =
    isRecord(output) && isRecord(output.draft) ? output.draft : null;
  const failure = isRecord(draft?.failure) ? draft.failure : null;
  const retry = isRecord(draft?.retry) ? draft.retry : null;
  return {
    reason: text(draft?.last_run_failed) ?? error,
    draftId: text(draft?.draft_id),
    title: text(draft?.title),
    keptDocument: draft?.has_docx === true,
    research: name === "start_research",
    code: text(failure?.code),
    retryable: failure?.retryable !== false,
    instructions: text(retry?.instructions)
  };
};

/** What Retry says as the attorney's next message. */
export const retryText = (
  failure: Pick<RunFailure, "title" | "research" | "instructions">
): string => {
  const what = failure.title ? `“${failure.title}”` : "it";
  if (failure.research) return `Retry the research for ${what}.`;
  return failure.instructions
    ? `Retry drafting ${what} with the same instructions.`
    : `Retry drafting ${what}.`;
};

type Props = {
  failure: RunFailure;
  onOpen?: (() => void) | null;
};

export const RunFailureCard: FC<Props> = ({ failure, onOpen }) => {
  const actions = useOfferActionsContext();
  const say = actions?.say;
  return (
    <div
      className="flex min-w-0 flex-col gap-2 rounded-xl bg-danger-soft px-3 py-2 text-sm"
      data-testid="run-failure"
      data-code={failure.code ?? undefined}
    >
      <div className="flex min-w-0 items-start gap-1.5" title={failure.reason}>
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-danger" />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-fg" data-testid="run-failure-reason">
            {friendlyRunFailure(failure.reason, failure.code)}
          </div>
          <div className="text-fg-3">
            {failure.keptDocument
              ? "The draft keeps its document as it was."
              : "Nothing was written."}
          </div>
          {failure.instructions ? (
            <div
              className="mt-1 line-clamp-2 text-fg-3"
              data-testid="run-failure-instructions"
            >
              Asked: {failure.instructions}
            </div>
          ) : null}
        </div>
      </div>
      <div className="-ml-1 flex items-center gap-1">
        {say && failure.retryable ? (
          <Button
            variant="secondary"
            size="xs"
            disabled={actions.busy}
            onClick={() => say(retryText(failure))}
            data-testid="run-failure-retry"
          >
            <RotateCcw className="size-3" /> Retry
          </Button>
        ) : null}
        {onOpen ? (
          <Button variant="ghost" size="xs" onClick={onOpen}>
            Open Draft <ExternalLink className="size-3" />
          </Button>
        ) : null}
      </div>
    </div>
  );
};
